import { useState, useRef } from 'react';
import { Camera, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { authAxios } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ImageUpload({ images, onImagesChange }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newImages = [...images];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await authAxios.post(`${API}/upload`, formData);
        newImages.push({
          path: data.path,
          filename: data.filename,
          content_type: data.content_type
        });
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    onImagesChange(newImages);
    setUploading(false);
  };

  const removeImage = (index) => {
    const updated = images.filter((_, i) => i !== index);
    onImagesChange(updated);
  };

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
        <Button
          type="button"
          data-testid="upload-gallery-btn"
          variant="outline"
          className="h-12 px-5 border-[#E5E5EA] text-[#1C1C1E] hover:bg-[#F2F2F7] rounded-xl"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Gallery
        </Button>
        <Button
          type="button"
          data-testid="upload-camera-btn"
          variant="outline"
          className="h-12 px-5 border-[#E5E5EA] text-[#1C1C1E] hover:bg-[#F2F2F7] rounded-xl"
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading}
        >
          <Camera className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Camera
        </Button>
      </div>

      {uploading && (
        <div className="text-sm text-[#8E8E93] mb-3">Uploading...</div>
      )}

      {images.length > 0 ? (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img, i) => (
            <div key={i} className="relative group aspect-square bg-[#F2F2F7] border border-[#E5E5EA] rounded-xl overflow-hidden">
              <img
                src={`${API}/files/${img.path}?auth=${localStorage.getItem('access_token')}`}
                alt={img.filename || 'Reference'}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                data-testid={`remove-image-${i}`}
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-[#1C1C1E]" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="border-2 border-dashed border-[#E5E5EA] bg-[#F2F2F7] p-8 flex flex-col items-center justify-center rounded-xl">
          <ImageIcon className="w-8 h-8 text-[#8E8E93] mb-2" strokeWidth={1.5} />
          <p className="text-sm text-[#8E8E93]">No reference images yet</p>
        </div>
      )}
    </div>
  );
}
